import type { Lang } from '../page';

interface HeroProps { lang: Lang; }

const badge: Record<Lang, string> = {
  en: 'HIGH-PERFORMANCE INDUSTRIAL MATERIAL',
  zh: '高性能工业材料',
  th: 'วัสดุอุตสาหกรรมประสิทธิภาพสูง',
  vi: 'VẬT LIỆU CÔNG NGHIỆP HIỆU SUẤT CAO',
  ms: 'BAHAN INDUSTRI BERPRESTASI TINGGI',
};
const title: Record<Lang, string> = {
  en: 'Polyimide Film', zh: '聚酰亚胺薄膜', th: 'ฟิล์มโพลีอิไมด์',
  vi: 'Màng Polyimide', ms: 'Filem Polyimide',
};
const titleSub: Record<Lang, string> = {
  en: '聚酰亚胺薄膜', zh: 'Polyimide Film', th: 'Polyimide Film',
  vi: 'Polyimide Film', ms: 'Polyimide Film',
};
const subtitle: Record<Lang, string> = {
  en: 'High Temperature · Superior Insulation · Ultra Stable',
  zh: '耐高温 · 卓越绝缘 · 超稳定',
  th: 'ทนอุณหภูมิสูง · ฉนวนชั้นเยี่ยม · เสถียรภาพสูง',
  vi: 'Chịu Nhiệt Cao · Cách Điện Vượt Trội · Siêu Ổn Định',
  ms: 'Tahan Suhu Tinggi · Penebat Unggul · Ultra Stabil',
};
const desc: Record<Lang, string> = {
  en: 'Engineered for aerospace, electronics, and new energy applications. Exceptional thermal stability, electrical insulation, and mechanical strength for demanding industrial environments.',
  zh: '专为航空航天、电子电器及新能源领域打造。卓越的热稳定性、电气绝缘性及机械强度，满足高要求工业环境的需求。',
  th: 'ออกแบบสำหรับอุตสาหกรรมการบินและอวกาศ อิเล็กทรอนิกส์ และพลังงานใหม่ ด้วยความเสถียรทางความร้อน ฉนวนไฟฟ้า และความแข็งแรงทางกลที่โดดเด่น',
  vi: 'Được thiết kế cho hàng không vũ trụ, điện tử và năng lượng mới. Độ ổn định nhiệt, cách điện và độ bền cơ học vượt trội cho môi trường công nghiệp khắc nghiệt.',
  ms: 'Direka untuk aeroangkasa, elektronik dan tenaga baharu. Kestabilan terma, penebat elektrik dan kekuatan mekanikal yang luar biasa untuk persekitaran industri yang mencabar.',
};
const getQuote: Record<Lang, string> = {
  en: 'Get Quote', zh: '获取报价', th: 'ขอใบเสนอราคา', vi: 'Nhận Báo Giá', ms: 'Dapatkan Sebut Harga',
};
const downloadDS: Record<Lang, string> = {
  en: 'Download Datasheet', zh: '下载数据手册',
  th: 'ดาวน์โหลดเอกสาร', vi: 'Tải Tài Liệu', ms: 'Muat Turun Helaian Data',
};
const heatRes: Record<Lang, string> = {
  en: 'Heat Resistance', zh: '耐热温度', th: 'ทนความร้อน', vi: 'Chịu Nhiệt', ms: 'Tahan Haba',
};
const minThk: Record<Lang, string> = {
  en: 'Min Thickness', zh: '最小厚度', th: 'ความหนาต่ำสุด', vi: 'Độ Dày Tối Thiểu', ms: 'Ketebalan Minimum',
};
const insGrade: Record<Lang, string> = {
  en: 'Insulation Grade', zh: '绝缘等级', th: 'ระดับฉนวน', vi: 'Cấp Cách Điện', ms: 'Gred Penebat',
};
const scrollDown: Record<Lang, string> = {
  en: 'Scroll Down', zh: '向下滚动', th: 'เลื่อนลง', vi: 'Cuộn Xuống', ms: 'Tatal Ke Bawah',
};
const certified: Record<Lang, string> = {
  en: 'Certified', zh: '认证', th: 'ได้รับการรับรอง', vi: 'Được Chứng Nhận', ms: 'Diperakui',
};

export default function Hero({ lang }: HeroProps) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9c28c1b058807cc00eeca2d17de0d25b.jpg"
          alt="Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center px-4 py-2 border-2 border-white/50 rounded-full">
              <span className="text-white text-xs font-medium tracking-wider">{badge[lang]}</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight">{title[lang]}</h1>
              <h2 className="text-3xl lg:text-4xl font-light text-gray-200">{titleSub[lang]}</h2>
              <h3 className="text-2xl lg:text-3xl font-semibold text-amber-400">{subtitle[lang]}</h3>
            </div>

            <p className="text-lg text-gray-200 leading-relaxed max-w-xl">{desc[lang]}</p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#purchase"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:shadow-2xl transition-all whitespace-nowrap"
              >
                <span>{getQuote[lang]}</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#download"
                className="inline-flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap"
              >
                <i className="ri-download-line"></i>
                <span>{downloadDS[lang]}</span>
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">400°C</div>
                <div className="text-sm text-amber-400 mt-1">{heatRes[lang]}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">0.025mm</div>
                <div className="text-sm text-amber-400 mt-1">{minThk[lang]}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">Class H</div>
                <div className="text-sm text-amber-400 mt-1">{insGrade[lang]}</div>
              </div>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/c1d369de78ba6b6a884b69ca21dbc9f2/4a8a49f82ba071f9dcd095d6bb2dd637.png"
                alt="Polyimide Film Product"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-full p-4 shadow-xl">
                <div className="text-center">
                  <div className="text-sm font-semibold text-gray-900">ISO 9001</div>
                  <div className="text-xs text-gray-600">{certified[lang]}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center space-y-2 animate-bounce">
          <span className="text-white text-sm">{scrollDown[lang]}</span>
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </div>
    </section>
  );
}
